import React from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';

const Showroom = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-black py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 text-center">
            Visit Our Showroom
          </h1>
          <p className="text-light max-w-3xl mx-auto text-center text-lg">
            Experience our handcrafted hats in person and meet the artisans behind Kala Hats at our Canggu workshop.
          </p>
        </div>
      </div>
      
      {/* Showroom Information */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/2">
              <h2 className="font-serif text-3xl font-bold text-primary mb-6">
                Our Bali Workshop
              </h2>
              
              <p className="text-light mb-4">
                Located in the heart of Canggu, our workshop is where all the magic happens. This is where our skilled artisans meticulously craft each Kala hat by hand, using traditional techniques passed down through generations.
              </p>
              
              <p className="text-light mb-4">
                When you visit our showroom, you'll have the opportunity to:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-light space-y-2">
                <li>See our complete collection of handcrafted hats</li>
                <li>Watch our artisans at work (on select days)</li>
                <li>Try on different styles and find your perfect fit</li>
                <li>Discuss custom designs with our hat makers</li>
                <li>Learn about the traditional crafting techniques we use</li>
              </ul>
              
              <div className="bg-black/30 p-6 rounded-lg border border-gray-700 mb-6">
                <h3 className="font-serif text-xl font-bold text-primary mb-3">
                  Showroom Hours
                </h3>
                <p className="text-light mb-2">Monday - Saturday: 9:00 AM - 5:00 PM</p>
                <p className="text-light mb-2">Sunday: Closed</p>
                <p className="text-light mb-4">Visits by appointment are recommended</p>
                
                <Button 
                  as="a" 
                  href="https://wa.me/6281289098912?text=I'd%20like%20to%20schedule%20a%20visit%20to%20your%20showroom" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  variant="primary"
                >
                  Schedule a Visit
                </Button>
              </div>
              
              <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-3">
                  Location
                </h3>
                <address className="not-italic text-light">
                  <p className="mb-1">Kala house by Sastrajiwa</p>
                  <p className="mb-1">Jl. Canggu Permai GG Cendrawasih VII No.15</p>
                  <p className="mb-1">Tibubeneng, Kec. Kuta Utara</p>
                  <p className="mb-1">Kabupaten Badung, Bali 80365</p>
                  <p className="mb-4">Indonesia</p>
                </address>
                
                <a 
                  href="https://maps.app.goo.gl/KXqvBf2skGnfauia7" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary hover:underline inline-block"
                >
                  View on Google Maps
                </a>
              </div>
            </div>
            
            <div className="lg:w-1/2">
              <div className="rounded-lg overflow-hidden mb-6 h-80">
                <img 
                  src="/images/showroom.jpg" 
                  alt="Kala Hats Workshop" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="rounded-lg overflow-hidden mb-6 h-80">
                <img 
                  src="/images/collection.jpg" 
                  alt="Hat Collection Display" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="rounded-lg overflow-hidden h-80">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3944.3735256442!2d115.14975!3d-8.6599!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd2388f1d1d3c7f%3A0x9a5a9c1d4d9c9a9a!2sJl.%20Canggu%20Permai%20GG%20Cendrawasih%20VII%20No.15%2C%20Tibubeneng%2C%20Kec.%20Kuta%20Utara%2C%20Kabupaten%20Badung%2C%20Bali%2080365!5e0!3m2!1sen!2sid!4v1650000000000!5m2!1sen!2sid" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Kala Hats Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Workshop Experience */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl font-bold text-primary mb-8 text-center">
            The Workshop Experience
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-secondary p-6 rounded-lg border border-gray-700">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">1</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-3 text-light text-center">
                Tour the Workshop
              </h3>
              <p className="text-gray-400">
                See where the magic happens. Our artisans will guide you through the workshop, explaining the traditional techniques and tools used in our hat-making process.
              </p>
            </div>
            
            <div className="bg-secondary p-6 rounded-lg border border-gray-700">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">2</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-3 text-light text-center">
                Try On Our Collection
              </h3>
              <p className="text-gray-400">
                Explore our complete collection of handcrafted hats. Our team will help you find the perfect style and size that complements your features and personal style.
              </p>
            </div>
            
            <div className="bg-secondary p-6 rounded-lg border border-gray-700">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary text-2xl font-bold">3</span>
              </div>
              <h3 className="font-serif text-xl font-bold mb-3 text-light text-center">
                Design Your Custom Hat
              </h3>
              <p className="text-gray-400">
                Discuss your ideas with our master hat makers. From material selection to unique embellishments, we'll help bring your vision to life with a truly one-of-a-kind hat.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Button 
              as="a" 
              href="https://wa.me/6281289098912?text=I'd%20like%20to%20schedule%20a%20visit%20to%20your%20showroom" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="primary"
              size="lg"
            >
              Book Your Visit
            </Button>
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl font-bold text-primary mb-8 text-center">
            What Our Visitors Say
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                  <span className="text-primary font-bold">SM</span>
                </div>
                <div>
                  <h3 className="font-bold text-light">Sarah M.</h3>
                  <p className="text-gray-400 text-sm">New York, USA</p>
                </div>
              </div>
              <p className="text-gray-400 italic">
                "Visiting the Kala Hats workshop was a highlight of my trip to Bali. Seeing the artisans at work and learning about the traditional techniques was fascinating. I left with a beautiful custom hat that I'll cherish forever."
              </p>
            </div>
            
            <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                  <span className="text-primary font-bold">JT</span>
                </div>
                <div>
                  <h3 className="font-bold text-light">James T.</h3>
                  <p className="text-gray-400 text-sm">Melbourne, Australia</p>
                </div>
              </div>
              <p className="text-gray-400 italic">
                "The attention to detail and craftsmanship at Kala Hats is exceptional. The team took the time to help me find the perfect hat for my face shape and style. The workshop itself is beautiful and worth a visit even if you're just browsing."
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-primary mb-6">
            Ready to Visit?
          </h2>
          <p className="text-light max-w-2xl mx-auto mb-8">
            We'd love to welcome you to our workshop in Canggu. Schedule a visit today to experience the artistry and craftsmanship behind Kala Hats.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              as="a" 
              href="https://wa.me/6281289098912?text=I'd%20like%20to%20schedule%20a%20visit%20to%20your%20showroom" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="primary"
            >
              Schedule a Visit
            </Button>
            <Button to="/contact" variant="outline">
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Showroom;
